import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import LeadCaptureForm from '../components/landing/LeadCaptureForm';
import {
  ArrowRight, ClipboardList, Package, FileCheck, Truck, BarChart3,
  Shield, Smartphone, Zap, Globe, CheckCircle2, Building2,
} from 'lucide-react';

const FEATURES = [
  {
    icon: ClipboardList,
    accent: '#ff6600',
    title: 'Work orders that flow',
    body: 'Capture, assign and track issues across every site. SLA breach detection runs every 15 minutes — your team sees a problem before the customer does.',
  },
  {
    icon: Package,
    accent: '#0891b2',
    title: 'Asset register with QR',
    body: 'Every chair, chiller and CCTV camera in one searchable register. Print QR codes once; scan-to-action forever.',
  },
  {
    icon: FileCheck,
    accent: '#16a34a',
    title: 'Audits & compliance',
    body: 'Configurable inspection templates. Photo evidence. Auto-generated certificates. Clean audit trails when the regulator turns up.',
  },
  {
    icon: Truck,
    accent: '#7c3aed',
    title: 'Vendor SLA scorecards',
    body: 'See which suppliers are pulling their weight. Branded PDF scorecards email automatically — vendors improve when they see the numbers.',
  },
  {
    icon: BarChart3,
    accent: '#111565',
    title: 'Dashboards that customise',
    body: 'Drag-and-drop widgets. Saved filter views. Cost-centre forecasts. The KPIs you actually care about, on the screen you actually open.',
  },
  {
    icon: Smartphone,
    accent: '#f59e0b',
    title: 'Field-ready on every device',
    body: 'Phone, iPad, Android tablet, Apple Watch on the way. Offline-first PWA + native iOS build. Works in the basement, syncs at the door.',
  },
];

const HIGHLIGHTS = [
  'JWT auth with optional MFA',
  'WCAG 2.2 AA self-audit',
  '17 backend pytests, 100% green',
  'UK English throughout',
  'Capacitor iOS build',
  'Branded vendor PDF exports',
];

const Landing = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0d3a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ff6600]" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-[#0a0d3a] text-white" data-testid="landing-page">
      {/* Top nav */}
      <nav className="border-b border-white/10 sticky top-0 z-30 backdrop-blur-md bg-[#0a0d3a]/85">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-gradient-to-br from-[#ff6600] to-[#ff8c33] flex items-center justify-center">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-base leading-none">FacilityPro</p>
              <p className="text-[10px] text-white/60 uppercase tracking-wider mt-0.5">Facility management</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button
                variant="ghost"
                className="text-white hover:bg-white/10 hover:text-white"
                data-testid="landing-login-link"
              >
                Sign in
              </Button>
            </Link>
            <a href="#contact">
              <Button className="bg-[#ff6600] hover:bg-[#e65a00] text-white" data-testid="landing-contact-cta">
                Request demo <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Subtle radial glow */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at top, rgba(255,102,0,0.18), transparent 60%), ' +
              'radial-gradient(ellipse at bottom right, rgba(17,21,101,0.5), transparent 70%)',
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-28 md:pt-32 md:pb-40">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs uppercase tracking-wider text-white/80 mb-6">
              <Zap className="h-3 w-3 text-[#ff6600]" />
              v1.1 — Now with AI photo classification
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              Modern CAFM, built<br />
              for <span className="text-[#ff6600]">field teams</span>.
            </h1>
            <p className="text-lg md:text-xl text-white/75 mt-6 max-w-2xl leading-relaxed">
              FacilityPro is a single tool for site audits, asset management, work
              orders, vendor SLAs and ESG tracking — designed mobile-first for
              technicians, scaled enterprise-grade for facility managers.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-9">
              <Link to="/login">
                <Button
                  size="lg"
                  className="bg-[#ff6600] hover:bg-[#e65a00] text-white text-base h-12 px-6"
                  data-testid="landing-hero-signin"
                >
                  Sign in <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <a href="#features">
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white text-base h-12 px-6"
                  data-testid="landing-hero-tour"
                >
                  Take the tour
                </Button>
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-10 text-xs text-white/60">
              {HIGHLIGHTS.map((h) => (
                <span key={h} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5 text-[#16a34a]" />
                  {h}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section
        id="features"
        className="bg-white text-foreground py-20 md:py-28"
        data-testid="landing-features"
      >
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.2em] text-[#ff6600] font-semibold">What's inside</p>
          <h2 className="text-3xl md:text-4xl font-bold mt-3 max-w-2xl leading-tight">
            Everything a facility team needs, nothing they don't.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div
                  key={f.title}
                  className="group rounded-xl border border-slate-200 bg-white p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all"
                  data-testid={`landing-feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                    style={{ background: `${f.accent}15` }}
                  >
                    <Icon className="h-5 w-5" style={{ color: f.accent }} />
                  </div>
                  <h3 className="font-semibold text-lg">{f.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="bg-[#f8fafc] py-16 md:py-20 text-foreground border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#ff6600] font-semibold">Built for production</p>
            <h2 className="text-2xl md:text-3xl font-bold mt-3 leading-tight">
              Hardened for enterprise rollout.
            </h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Role-based access control with an editable permissions matrix. JWT
              auth with optional time-based MFA. Audit logs on every sensitive
              action. Resend-powered email digests, FCM push, scheduled vendor
              scorecards.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { Icon: Shield, label: 'JWT + MFA', tone: '#111565' },
              { Icon: BarChart3, label: 'Cost forecasts', tone: '#0891b2' },
              { Icon: Truck, label: 'Vendor SLA', tone: '#ff6600' },
              { Icon: Globe, label: 'WCAG 2.2 AA', tone: '#16a34a' },
            ].map((b) => (
              <div key={b.label} className="rounded-lg border border-slate-200 bg-white p-4">
                <b.Icon className="h-5 w-5 mb-2" style={{ color: b.tone }} />
                <p className="font-semibold text-sm">{b.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA / Contact */}
      <section
        id="contact"
        className="py-20 md:py-28"
        data-testid="landing-contact-section"
      >
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold leading-tight">
              Ready to bring your team on?
            </h2>
            <p className="text-white/75 mt-4 leading-relaxed">
              Tell us a bit about you and we'll be in touch within one business day.
              Already a customer? <Link to="/login" className="text-[#ff6600] hover:underline">Sign in here</Link>.
            </p>
          </div>
          <LeadCaptureForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 text-xs text-white/50">
        <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p>&copy; {new Date().getFullYear()} FacilityPro. All rights reserved.</p>
          <div className="flex gap-5">
            <Link to="/login" className="hover:text-white/80 transition-colors">Sign in</Link>
            <a href="#contact" className="hover:text-white/80 transition-colors">Request demo</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
